import React, { FC, useMemo, useState } from 'react'
import { Button, Well, Drawer } from '@pushly/aqe/lib/components'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { isDef } from '@pushly/aqe/lib/utils/is-def'
import titleCase from 'title-case'
import clone from 'clone'
import * as deepEqual from 'fast-deep-equal'
import * as randomstring from 'randomstring'
import { DomainDto } from '../../dtos/domain'
import { AppService, DomainService } from '../../services'
import {
    calculateToPromptFcapSeconds,
    getFcapAndReferrerConfigs,
    prepareGlobalPromptSettings,
} from '../../_utils/fcap-and-referrer-configs'
import { getClassNames } from '../../_utils/classnames'
import { numberWithCommas, simpleFormErrorNotification, simpleNotification } from '../../_utils/utils'
import { PromptReferrerAllowType } from '../../dtos/domain'
import { TypedTagSelect } from '../typed-tag-select/typed-tag-select'
import { StatusBadge } from '@pushly/aqe/lib/components'
import { Divider, Flex, Form, Input, Radio, Select, Skeleton, Switch, notification, Space, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import { useForm } from 'antd/es/form/Form'
import { SETTINGS_WELL_FORM_LAYOUT } from '../../theme/constants'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'

interface IDomainPromptEditor {
    domain: DomainDto
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

export const DomainPromptEditor: FC<IDomainPromptEditor> = (props) => {
    const appSvc = useService(AppService)
    const domainSvc = useService(DomainService)

    const [form] = useForm()
    const [isDirty, setIsDirty] = useState(false)

    const currentValues = (formValues: any) => {
        const values: Partial<DomainDto> = {}
        // const formValues = form.getFieldsValue(true)

        const currFcapsEnabled = formValues.fcapsEnabled
        if (currFcapsEnabled) {
            values.frequencyCaps = {
                prompt: {
                    occurrences: formValues.promptFcapOccurrences,
                    fcapSeconds: calculateToPromptFcapSeconds(formValues),
                    displayMetric: formValues.promptFcapDisplayMetric,
                },
            }
        } else {
            values.frequencyCaps = null as any
        }
        if (deepEqual(values.frequencyCaps, props.domain.frequencyCaps)) {
            delete values.frequencyCaps
        }

        const globalPromptSettings = prepareGlobalPromptSettings(formValues, clone(props.domain.globalPromptSettings))
        if (!deepEqual(globalPromptSettings, props.domain.globalPromptSettings)) {
            values.globalPromptSettings = globalPromptSettings
        }

        return values
    }

    const fcapsEnabled = Form.useWatch('fcapsEnabled', form) ?? !!props.domain.frequencyCaps?.prompt
    const referrerConditionsEnabled =
        Form.useWatch('referrerConditionsEnabled', form) ?? !!props.domain.globalPromptSettings?.referrers?.enabled

    const config = getFcapAndReferrerConfigs(props.domain)

    const handleSubmit = async () => {
        try {
            const formValues = await form.validateFields()
            const values = currentValues(formValues)

            const { ok, data } = await domainSvc.updateDomainById(props.domain.id, values)

            if (ok) {
                props.onDomainUpdated(data)
                props.onClose(null)
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    const handleValuesChange = () => {
        const currentFields = form.getFieldsValue(true)
        const changedValues = currentValues(currentFields)
        setIsDirty(Object.keys(changedValues).length > 0)
    }

    return (
        <Drawer
            getContainer={appSvc.getAppContainer}
            className={getClassNames('ddf-prompt-settings-editor')}
            title="Update General Settings"
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            disableSubmit={!isDirty}
            onClose={props.onClose}
            open={props.visible}
            width={800}
        >
            <Form
                form={form}
                initialValues={{
                    fcapsEnabled: !!props.domain.frequencyCaps?.prompt,
                    promptFcapOccurrences: config.defaultPromptFcapOccurrences,
                    promptFcap: config.defaultPromptFcapFrequency,
                    promptFcapDisplayMetric: config.defaultPromptFcapDisplayMetric,
                    referrerConditionsEnabled: !!props.domain.globalPromptSettings?.referrers?.enabled,
                    referrerWhenBlank: config.defaultReferrerWhenBlank,
                    referrerAllow: config.defaultReferrerAllowValue,
                    referrerBlock: config.defaultReferrerBlockValue,
                }}
                onValuesChange={handleValuesChange}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                requiredMark={false}
                size="small"
                labelAlign="left"
            >
                <Well title="Frequency Caps" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item label="Prompt Frequency Cap" name="fcapsEnabled" valuePropName="checked">
                        <Switch aria-label="Enable Prompt Frequency Cap" />
                    </Form.Item>

                    {fcapsEnabled && (
                        <Form.Item>
                            <Flex vertical gap={8}>
                                <Space size="small">
                                    <Typography.Text>A visitor to my site should be prompted</Typography.Text>
                                    <Space.Compact>
                                        <Form.Item
                                            name="promptFcapOccurrences"
                                            rules={[
                                                {
                                                    pattern: /^[\d]+$/i,
                                                    message: 'Occurrence count must be numeric',
                                                },
                                                {
                                                    validator: (rule, value, callback) => {
                                                        value >= 1
                                                            ? callback()
                                                            : callback(new Error(String(rule.message)).message)
                                                    },
                                                    message: 'Occurrence count must be 1 or greater',
                                                },
                                            ]}
                                            noStyle
                                        >
                                            <Input
                                                size="small"
                                                style={{ width: 60 }}
                                                placeholder="1"
                                                autoComplete="off"
                                            />
                                        </Form.Item>
                                        <Space.Addon>time(s) every</Space.Addon>
                                    </Space.Compact>
                                    <Space.Compact>
                                        <Form.Item
                                            name="promptFcap"
                                            rules={[
                                                {
                                                    pattern: /^[\d]+$/i,
                                                    message: 'Frequency must be numeric',
                                                },
                                                {
                                                    validator: (rule, value, callback) => {
                                                        value >= 1 && value <= 3650
                                                            ? callback()
                                                            : callback(new Error(String(rule.message)).message)
                                                    },
                                                    message: 'Frequency must be between 1 and 3,650',
                                                },
                                            ]}
                                            noStyle
                                        >
                                            <Input
                                                size="small"
                                                style={{ width: 60 }}
                                                placeholder="1"
                                                autoComplete="off"
                                            />
                                        </Form.Item>
                                        <Form.Item name="promptFcapDisplayMetric" noStyle>
                                            <Select
                                                size="small"
                                                style={{ width: 100 }}
                                                options={[
                                                    { value: 'days', label: 'day(s)' },
                                                    { value: 'hours', label: 'hour(s)' },
                                                    { value: 'minutes', label: 'minute(s)' },
                                                ]}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Space>
                            </Flex>
                        </Form.Item>
                    )}

                    <Space align="start">
                        <InfoCircleOutlined className="info-icon" />
                        <Typography.Paragraph type="secondary" className="font-size--s" style={{ marginBottom: 0 }}>
                            The frequency cap will determine how often a user that does not interact (dismiss or allow)
                            with a prompt will be prompted again to subscribe.
                        </Typography.Paragraph>
                    </Space>
                </Well>

                <Divider />

                <Well title="Referrer Conditions" mode="ghost" hideFooter={true} flush>
                    <Form.Item
                        label="Prompt Referrer Conditions"
                        name="referrerConditionsEnabled"
                        valuePropName="checked"
                    >
                        <Switch aria-label="Enable Prompt Referrer Conditions" />
                    </Form.Item>

                    {referrerConditionsEnabled && (
                        <Flex vertical gap={16}>
                            <Form.Item label="When referrer is blank?" name="referrerWhenBlank">
                                <Radio.Group
                                    optionType="button"
                                    buttonStyle="solid"
                                    size="small"
                                    options={[
                                        {
                                            value: PromptReferrerAllowType.IGNORE,
                                            label: PromptReferrerAllowType.IGNORE,
                                        },
                                        {
                                            value: PromptReferrerAllowType.ALLOW,
                                            label: PromptReferrerAllowType.ALLOW,
                                        },
                                        {
                                            value: PromptReferrerAllowType.BLOCK,
                                            label: PromptReferrerAllowType.BLOCK,
                                        },
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item label="Allowed Referrers" name="referrerAllow">
                                <TypedTagSelect types={['exact', 'contains']} />
                            </Form.Item>

                            <Form.Item label="Blocked Referrers" name="referrerBlock">
                                <TypedTagSelect types={['exact', 'contains']} />
                            </Form.Item>
                        </Flex>
                    )}

                    <Space align="start">
                        <InfoCircleOutlined className="info-icon" />
                        <Typography.Paragraph type="secondary" className="font-size--s" style={{ marginBottom: 0 }}>
                            Referrer conditions determine if a user is able to see a prompt based on where they arrived
                            from.
                        </Typography.Paragraph>
                    </Space>
                </Well>
            </Form>
        </Drawer>
    )
}

interface IGeneralSettingsWell {
    loading?: boolean
    domain: DomainDto
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IState {
    showDrawer: boolean
    drawerKey?: string
}

export class GeneralSettingsWell extends React.Component<IGeneralSettingsWell, IState> {
    public state: IState = {
        showDrawer: false,
    }

    public render() {
        return (
            <Well
                className="nested"
                title="General Settings"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            <Form
                                {...SETTINGS_WELL_FORM_LAYOUT}
                                colon={false}
                                requiredMark={false}
                                labelAlign="left"
                                size="small"
                            >
                                {this.renderFrequencyCaps()}

                                <Divider className="inner-well-divider" />

                                {this.renderReferrerConditions()}
                            </Form>

                            <DomainPromptEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.domain}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderFrequencyCaps() {
        const enabled = !!this.domain.frequencyCaps?.prompt
        const config = getFcapAndReferrerConfigs(this.domain)

        return (
            <Well title="Frequency Caps" mode="ghost" hideFooter={true} padding="none">
                <Form.Item>
                    <StatusBadge type="generic" expanded={true} status={enabled ? 'ENABLED' : 'DISABLED'} />
                </Form.Item>

                {enabled && (
                    <Form.Item label="Prompt visitors to my site">
                        <Typography.Text>
                            {config.defaultPromptFcapOccurrences} time(s) every{' '}
                            {numberWithCommas(config.defaultPromptFcapFrequency)}{' '}
                            {config.defaultPromptFcapDisplayMetric.replace(/s$/, '(s)')}
                        </Typography.Text>
                    </Form.Item>
                )}
            </Well>
        )
    }

    protected renderReferrerConditions() {
        const enabled = this.domain.globalPromptSettings?.referrers?.enabled ?? false
        const config = getFcapAndReferrerConfigs(this.domain)

        return (
            <Well title="Referrer Conditions" mode="ghost" hideFooter={true} padding="none">
                <Form.Item>
                    <StatusBadge type="generic" expanded={true} status={enabled ? 'ENABLED' : 'DISABLED'} />
                </Form.Item>

                {enabled && (
                    <>
                        <Form.Item label="Blank Referrer Behavior">
                            <Typography.Text>{titleCase(config.defaultReferrerWhenBlank)}</Typography.Text>
                        </Form.Item>

                        <Form.Item label="Allowed Referrers">
                            {!isDef(config.defaultReferrerAllowValue) ? (
                                <Typography.Text>All Referrers</Typography.Text>
                            ) : (
                                config.defaultReferrerAllowValue.map((ref: any) => (
                                    <Typography.Text key={ref.value}>
                                        {ref.type}: {ref.value}
                                    </Typography.Text>
                                ))
                            )}
                        </Form.Item>

                        <Form.Item label="Blocked Referrers">
                            {!isDef(config.defaultReferrerBlockValue) ? (
                                <Typography.Text>No Referrers</Typography.Text>
                            ) : (
                                config.defaultReferrerBlockValue.map((ref: any) => (
                                    <Typography.Text key={ref.value}>
                                        {ref.type}: {ref.value}
                                    </Typography.Text>
                                ))
                            )}
                        </Form.Item>
                    </>
                )}
            </Well>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
